import type { ShellUrlConfig } from './ShellUrlConfigService';

interface IncomingLauncherMessage {
    readonly type: string;
    readonly payload?: unknown;
    readonly sessionId?: string;
}

interface StreamApplicationPayload {
    readonly type?: unknown;
    readonly reason?: unknown;
    readonly userStatus?: unknown;
    readonly publisherUserId?: unknown;
}

interface AnalyticsBridgeMessage {
    readonly type: 'analyticsEvent';
    readonly source: 'APPSHELL';
    readonly eventName: string;
    readonly eventPayload: Record<string, unknown>;
}

interface RelaySecurityContext {
    readonly currentSessionId: string;
    readonly trustedParentOrigin: string;
}

type ForwardToStreamFn = (bytes: Uint8Array) => boolean;
type SessionGetterFn = () => string;
type SecurityErrorFn = (message: string) => void;

const MAX_MESSAGE_SIZE = 8192;

export class ParentMessageRelayService {
    private readonly allowedOrigins: ReadonlySet<string>;
    private readonly config: ShellUrlConfig;
    private readonly onSecurityError: SecurityErrorFn;
    private readonly handleMessageBound: (event: MessageEvent<unknown>) => void;
    private readonly encoder: TextEncoder;
    private trustedParentOrigin: string;
    private trustedParentSource: Window | null;
    private closeDispatchLockedUntilMs: number;
    private forwardToStream: ForwardToStreamFn;
    private getCurrentSessionId: SessionGetterFn;

    constructor(args: {
        allowedOrigins: ReadonlySet<string>;
        config: ShellUrlConfig;
        onSecurityError: SecurityErrorFn;
    }) {
        this.allowedOrigins = args.allowedOrigins;
        this.config = args.config;
        this.onSecurityError = args.onSecurityError;
        this.encoder = new TextEncoder();
        this.trustedParentOrigin = '';
        this.trustedParentSource = null;
        this.closeDispatchLockedUntilMs = 0;
        this.forwardToStream = () => false;
        this.getCurrentSessionId = () => '';
        this.handleMessageBound = this.handleMessage.bind(this);
    }

    public start(forwardToStream: ForwardToStreamFn, getCurrentSessionId: SessionGetterFn): void {
        this.forwardToStream = forwardToStream;
        this.getCurrentSessionId = getCurrentSessionId;
        window.addEventListener('message', this.handleMessageBound);
    }

    public stop(): void {
        window.removeEventListener('message', this.handleMessageBound);
        this.trustedParentOrigin = '';
        this.trustedParentSource = null;
        this.closeDispatchLockedUntilMs = 0;
    }

    public relayApplicationMessage(message: Uint8Array): void {
        const context = this.getRelaySecurityContext();
        if (!context) {
            this.onSecurityError('Blocked app->parent relay: no trusted parent origin/session context');
            return;
        }

        const parsed = this.parseApplicationPayload(message);
        if (!parsed) {
            return;
        }

        const payload = this.buildEnrichedPayload(parsed);
        try {
            window.parent.postMessage(payload, context.trustedParentOrigin);
        } catch (_error) {
            this.onSecurityError('Blocked app->parent relay: postMessage failed');
        }
    }

    public notifyCloseEvent(_reason: string): boolean {
        const nowMs = Date.now();
        if (nowMs < this.closeDispatchLockedUntilMs) {
            // Caller contract: true only when this invocation actually posted a close.
            return false;
        }

        const trustedOrigin = this.getTrustedParentOrigin();
        if (!trustedOrigin || !this.allowedOrigins.has(trustedOrigin)) {
            this.onSecurityError('Blocked close relay: no trusted parent origin');
            return false;
        }

        if (this.postCloseToTrustedSource(trustedOrigin)) {
            this.closeDispatchLockedUntilMs = nowMs + 500;
            return true;
        }

        const fallbackWindows = this.getFallbackCloseWindows();
        for (const targetWindow of fallbackWindows) {
            if (this.postCloseToTarget(targetWindow, trustedOrigin)) {
                this.closeDispatchLockedUntilMs = nowMs + 500;
                return true;
            }
        }

        this.onSecurityError('Blocked close relay: postMessage failed for all trusted targets');
        return false;
    }

    public relayAnalyticsEvent(eventName: string, eventPayload: Record<string, unknown>): void {
        const normalizedEventName = eventName.trim();
        if (!normalizedEventName) {
            this.onSecurityError('Blocked analytics relay: empty event name');
            return;
        }

        const trustedOrigin = this.getTrustedParentOrigin();
        if (!trustedOrigin || !this.allowedOrigins.has(trustedOrigin)) {
            this.onSecurityError('Blocked analytics relay: no trusted parent origin');
            return;
        }

        const analyticsMessage: AnalyticsBridgeMessage = {
            type: 'analyticsEvent',
            source: 'APPSHELL',
            eventName: normalizedEventName,
            eventPayload: {
                // Spread caller payload first; config values follow to prevent spoofing
                ...eventPayload,
                publisherId: this.config.publisherId,
                publisherProductId: this.config.publisherProductId,
            }
        };

        try {
            window.parent.postMessage(analyticsMessage, trustedOrigin);
        } catch (_error) {
            this.onSecurityError('Blocked analytics relay: postMessage failed');
        }
    }

    private getRelaySecurityContext(): RelaySecurityContext | null {
        const currentSessionId = this.getCurrentSessionId();
        if (!currentSessionId) {
            return null;
        }

        const trustedParentOrigin = this.getTrustedParentOrigin();
        if (!trustedParentOrigin) {
            return null;
        }

        return {
            currentSessionId,
            trustedParentOrigin
        };
    }

    private getTrustedParentOrigin(): string {
        if (!this.trustedParentOrigin) {
            const originFromReferrer = this.resolveOriginFromReferrer();
            if (originFromReferrer) {
                this.trustedParentOrigin = originFromReferrer;
            }
        }

        return this.trustedParentOrigin;
    }

    private postCloseToTrustedSource(targetOrigin: string): boolean {
        if (!this.trustedParentSource || !this.trustedParentOrigin) {
            return false;
        }

        if (this.trustedParentOrigin !== targetOrigin) {
            return false;
        }

        return this.postCloseToTarget(this.trustedParentSource, targetOrigin);
    }

    private getFallbackCloseWindows(): Window[] {
        const fallbackWindows: Window[] = [];
        const candidates: Array<Window | null | undefined> = [window.parent, window.parent?.parent];
        for (const candidate of candidates) {
            if (!candidate || candidate === window || fallbackWindows.includes(candidate)) {
                continue;
            }

            fallbackWindows.push(candidate);
        }

        return fallbackWindows;
    }

    private postCloseToTarget(target: Window, targetOrigin: string): boolean {
        try {
            target.postMessage('close', targetOrigin);
            return true;
        } catch (_error) {
            return false;
        }
    }

    private isWindowSource(source: MessageEventSource): source is Window {
        if (!source || typeof source !== 'object') {
            return false;
        }

        const sourceRecord = source as unknown as { postMessage?: unknown };

        try {
            return typeof sourceRecord.postMessage === 'function';
        } catch (_error) {
            return false;
        }
    }

    private resolveOriginFromReferrer(): string {
        const referrer = document.referrer;
        if (!referrer) {
            return '';
        }

        try {
            const origin = new URL(referrer).origin;
            if (!this.allowedOrigins.has(origin)) {
                return '';
            }

            return origin;
        } catch (_error) {
            return '';
        }
    }

    private handleMessage(event: MessageEvent<unknown>): void {
        if (!this.allowedOrigins.has(event.origin)) {
            this.onSecurityError(`Blocked parent->app relay: origin not allowed (${event.origin})`);
            return;
        }

        const message = this.parseIncomingMessage(event.data);
        if (!message) {
            this.onSecurityError('Blocked parent->app relay: invalid message schema');
            return;
        }

        const currentSessionId = this.getCurrentSessionId();
        if (!currentSessionId) {
            this.onSecurityError('Blocked parent->app relay: no active session');
            return;
        }

        if (message.sessionId && message.sessionId !== currentSessionId) {
            this.onSecurityError('Blocked parent->app relay: sessionId mismatch');
            return;
        }

        this.trustedParentOrigin = event.origin;
        if (event.source && this.isWindowSource(event.source)) {
            this.trustedParentSource = event.source;
        }

        const encoded = this.encoder.encode(JSON.stringify(message));
        if (encoded.byteLength > MAX_MESSAGE_SIZE) {
            this.onSecurityError('Blocked parent->app relay: payload too large');
            return;
        }

        const sent = this.forwardToStream(encoded);
        if (!sent) {
            this.onSecurityError('Blocked parent->app relay: stream send failed');
        }
    }

    private parseIncomingMessage(data: unknown): IncomingLauncherMessage | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const record = data as Record<string, unknown>;
        if (typeof record.type !== 'string' || record.type.trim().length === 0) {
            return null;
        }

        if (record.sessionId !== undefined && typeof record.sessionId !== 'string') {
            return null;
        }

        return {
            type: record.type,
            payload: record.payload,
            sessionId: typeof record.sessionId === 'string' ? record.sessionId : undefined
        };
    }

    private parseApplicationPayload(message: Uint8Array): StreamApplicationPayload | null {
        let decoded = '';
        try {
            const decoder = new TextDecoder('utf-8', { fatal: false });
            decoded = decoder.decode(message);
        } catch (_error) {
            return null;
        }

        if (!decoded.trim()) {
            return null;
        }

        try {
            const parsed = JSON.parse(decoded) as Record<string, unknown>;
            return {
                type: parsed.type,
                reason: parsed.reason,
                userStatus: parsed.userStatus,
                publisherUserId: parsed.publisherUserId
            };
        } catch (_error) {
            return null;
        }
    }

    private buildEnrichedPayload(payload: StreamApplicationPayload): Record<string, unknown> {
        const { parsed, failed } = this.parseUserStatus(payload.userStatus);
        const result: Record<string, unknown> = {
            type: payload.type,
            reason: payload.reason,
            userStatus: parsed,
            publisherUserId: payload.publisherUserId,
            source: 'APPSHELL',
            ad_platform: 'playerwon',
            publisherId: this.config.publisherId,
            publisherProductId: this.config.publisherProductId,
            glftApplicationId: this.config.applicationId,
            glftStreamGroupId: this.config.streamGroupId
        };
        if (failed && typeof payload.userStatus === 'string') {
            result.userStatusParseError = true;
            result.userStatusRaw = String(payload.userStatus).slice(0, 512);
        }
        return result;
    }

    private parseUserStatus(input: unknown): { parsed: unknown; failed: boolean } {
        if (typeof input !== 'string') {
            return { parsed: input, failed: false };
        }

        try {
            return { parsed: JSON.parse(input), failed: false };
        } catch (_strictError) {
            // strict parse failed; attempt guarded quote-fix for object-like strings only
        }

        const trimmed = input.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            const correctedJson = trimmed
                .replace(/([{,]\s*)'([^']+)'\s*:/g, '$1"$2":')
                .replace(/:\s*'([^']*)'(\s*[},])/g, ':"$1"$2');
            try {
                return { parsed: JSON.parse(correctedJson), failed: false };
            } catch (_correctedError) {
                // guarded quote-fix also failed; fall through to failure path
            }
        }

        this.onSecurityError(`userStatus parse failed; raw length=${input.length}`);
        return { parsed: { currentLevel: '', currentScore: 0 }, failed: true };
    }
}
